/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Dependency-free color mode (#682). Chakra v3's `_dark` condition keys on a
 * `.dark` class on an ancestor (`dark => ".dark &, ..."`), and Chakra injects
 * the `_dark` token overrides into the runtime stylesheet regardless of any
 * provider — so toggling `.dark` on <html> is all that's needed to flip every
 * theme-aware token. No `next-themes` dependency.
 *
 * A tiny pre-paint script in index.html sets the class before React mounts to
 * avoid a flash; this provider keeps React state in sync and persists choice.
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { IconButton } from '@chakra-ui/react';
import type { IconButtonProps } from '@chakra-ui/react';
import { Moon, Sun } from 'lucide-react';

export type ColorMode = 'light' | 'dark';

export const COLOR_MODE_STORAGE_KEY = 'adminConsole.colorMode';

function applyColorMode(mode: ColorMode) {
  const root = document.documentElement;
  root.classList.toggle('dark', mode === 'dark');
  root.style.colorScheme = mode;
}

interface ColorModeContextValue {
  colorMode: ColorMode;
  setColorMode: (mode: ColorMode) => void;
  toggleColorMode: () => void;
}

const ColorModeContext = createContext<ColorModeContextValue | undefined>(undefined);

export const ColorModeProvider = ({ children }: { children: ReactNode }) => {
  // The pre-paint script already stamped the class; read from it so state and
  // DOM agree on first render (no flash, no hydration-style mismatch).
  const [colorMode, setColorModeState] = useState<ColorMode>(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'dark' : 'light',
  );

  const persist = (mode: ColorMode) => {
    try {
      localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
    } catch {
      /* storage unavailable — session-only is fine */
    }
  };

  const setColorMode = useCallback((mode: ColorMode) => {
    setColorModeState(mode);
    applyColorMode(mode);
    persist(mode);
  }, []);

  const toggleColorMode = useCallback(() => {
    setColorModeState((prev) => {
      const next: ColorMode = prev === 'dark' ? 'light' : 'dark';
      applyColorMode(next);
      persist(next);
      return next;
    });
  }, []);

  // Reconcile once on mount in case storage and the pre-paint heuristic drifted.
  useEffect(() => {
    applyColorMode(colorMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ColorModeContext.Provider value={{ colorMode, setColorMode, toggleColorMode }}>
      {children}
    </ColorModeContext.Provider>
  );
};

export function useColorMode(): ColorModeContextValue {
  const ctx = useContext(ColorModeContext);
  if (!ctx) {
    throw new Error('useColorMode must be used within a ColorModeProvider');
  }
  return ctx;
}

/** Header toggle button — sun in dark mode (→ go light), moon in light mode. */
export const ColorModeButton = (props: Partial<IconButtonProps>) => {
  const { colorMode, toggleColorMode } = useColorMode();
  const nextLabel = colorMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';
  return (
    <IconButton
      aria-label={nextLabel}
      title={nextLabel}
      size="sm"
      variant="ghost"
      color="fg.muted"
      _hover={{ bg: 'bg.subtle', color: 'fg.title' }}
      onClick={toggleColorMode}
      {...props}
    >
      {colorMode === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
    </IconButton>
  );
};
